import { GroupBase, OnChangeValue } from "react-select";
import { OptionType } from "../components/form/SearchableSelect";
import {Nullable} from "../models/common/Nullable";
import {Language} from "../config/LanguageConfig";
import {ValidationResult} from "../models/common/ValidationResult";

/**
 * validation error interface
 */
export interface ValidationError {
    hasError: boolean
    errorText: string
}

/**
 * date validation options to further narrow down the valid dates
 */
export interface DateValidationOptions {
    isRequired?: boolean
    laterThan?: Nullable<Date>[],
    earlierThan?: Nullable<Date>[],
    customValidation?: (date: Nullable<Date>) => ValidationError
}

/**
 * checks if time is valid ( 24 hour cycle )
 *
 * @param date
 * @param options
 * @param language
 * @param callback
 */
export const isValidTime = ( date: Nullable<Date>, options: DateValidationOptions, language: Language, callback: ( error: ValidationError ) => void ) => {

    let validation: ValidationError = { hasError: false, errorText: "" };

    if( date === null || isNaN(date.getTime()) ) {
        validation = { hasError: true, errorText: language.errors.form.timeNotParseable.errorText };
    } else {

        if (options.laterThan) {
            options.laterThan.forEach(currentDate => {
                if (currentDate === null || currentDate > date) {
                    validation = { hasError: true, errorText:language.errors.form.timeLaterThan( date ) };
                }
            });
        }
        if (options.earlierThan) {
            options.earlierThan.forEach(currentDate => {
                if (currentDate === null || currentDate < date) {
                    validation = { hasError: true, errorText:language.errors.form.timeEarlierThan( date ) };
                }
            });
        }
    }

    //if isValid function is set, that function will be called to validate the date picker
    if( typeof options.customValidation !== "undefined") {
        const customValidation: ValidationError = options.customValidation(date);

        if( customValidation.hasError) {
            validation = customValidation;
        }
    }

    if( ! options.isRequired && date === null  ) {
        validation = { hasError: false, errorText: "" };
    }

    callback( validation );
};

/**
 * checks if date is valid
 *
 * @param date
 * @param options options to check if the date meets als criteria
 * @param language currently selected language
 * @param callback
 */
export const isValidDate = ( date: Nullable<Date>, options: DateValidationOptions, language: Language, callback: ( error: ValidationError ) => void ) => {

    let validation: ValidationError = { hasError: false, errorText: "" };

    if( date === null || isNaN(date.getTime()) ) {
        validation = { hasError: true, errorText: language.errors.form.dateNotParseable.errorText };
    } else {

        if (options.laterThan) {
            options.laterThan.forEach(currentDate => {
                if ( currentDate === null || currentDate > date) {
                    validation = { hasError: true, errorText:language.errors.form.dateLaterThan( date ) };
                }
            });
        }
        if (options.earlierThan) {
            options.earlierThan.forEach(currentDate => {
                if (currentDate === null || currentDate < date) {
                    validation = { hasError: true, errorText:language.errors.form.dateEarlierThan( date ) };
                }
            });
        }
    }

    //if isValid function is set, that function will be called to validate the date picker
    if( typeof options.customValidation !== "undefined") {
        const customValidation: ValidationError = options.customValidation(date);

        if( customValidation.hasError) {
            validation = customValidation;
        }
    }

    if( ! options.isRequired && date === null  ) {
        validation = { hasError: false, errorText: "" };
    }

    callback( validation );
};

/**
 * checks if the string has a valid german number format with 
 * points for thousands and comma for decimal seperation
 * @param val the string to check
 */
export const isValidNumberString = ( val: string ) => {
    const pattern = /^-?\d{1,3}(?:\.\d{3})*(?:,\d{0,2})?$/g;
    return val.search( pattern ) !== -1;
};

/**
 * checks if value is not undefined, not null and if the selected option is included in the given options
 * 
 * @param value an object of { label: string, value: string }
 * @param options array of above objects
 */
export const isValidOption = ( value: OnChangeValue< OptionType, any >, options: readonly OptionType[] | readonly GroupBase< OptionType >[] | undefined ) => {
    let isValid = false;
    if( typeof value !== 'undefined' && value !== null && typeof options !== 'undefined'  ) {
        if( isOptionTypeArray( options )) {
            if( isOptionTypeArray( value ) ) {
                if( value.length === 0 ) {
                    isValid = true;
                } else {

                    options.forEach(option => {

                        if (value.some(valOpt => valOpt.value === option.value)) {
                            isValid = true;
                        }
                    });
                }
            } else {

                    options.forEach( option => {
                        if( option.value === value.value ) {
                            isValid = true;
                        }
                    });
            }
        }

    }
    return isValid;
};

/**
 * Determines if the provided option is an array of `OptionType`.
 *
 * This function is used to check whether the input data represents a flat array
 * of selectable options (`OptionType[]`). It simplifies validation when working
 * with user-selectable options in dropdowns or similar components.
 *
 * The check ensures the provided `option` is an array while accounting for immutability
 * via `readonly` types, improving type safety in scenarios where mutable operations
 * are not intended.
 *
 * @param option - The value to check. Can be a single `OptionType`, a readonly array
 *                 of `OptionType`, or a readonly array of `GroupBase<OptionType>`.
 * @returns True if the provided option is a readonly array of `OptionType`; otherwise, false.
 * @throws No exceptions are thrown by this function.
 */
export const isOptionTypeArray = ( option: OptionType | readonly OptionType[] | readonly GroupBase< OptionType >[] ): option is readonly OptionType[]  => {
    return Array.isArray( option )
};

/**
 * Validates if a given value exceeds a specified maximum value.
 * Ensures that input constraints are respected to maintain consistent user data.
 *
 * This validator compares the `currentValue` against the specified `maxValue`.
 * If the `currentValue` exceeds the `maxValue`, an error text is returned in the specified language.
 * Otherwise, it confirms the input as valid without an error.
 *
 * @param maxValue - The maximum allowable value.
 * @param currentValue - The value to validate against the `maxValue`.
 * @param language - The language object containing error message templates.
 * @return An object containing `errorText` (localized error message or empty string) and `isValid` (boolean indicating whether the validation passed).
 */
export function maxValueValidator(
    maxValue: number,
    currentValue: number,
    language: Language
): ValidationResult {

    if( currentValue > maxValue ) {
        return {
            errorText: language.errors.form.overMax.errorText.replace("$$[max]", maxValue.toString() ),
            isValid: false
        };
    }

    return { errorText: "", isValid: true };

}

/**
 * Validates whether a given value meets a specified minimum threshold.
 * This function ensures forms or inputs maintain a minimum value requirement for data integrity.
 *
 * Accepts a minimum value, the current value to validate, and a language configuration object for localized error messaging.
 * If the `currentValue` is less than the `minValue`, the function returns an object detailing the error and its invalid status.
 * Otherwise, it confirms validity without an error message.
 *
 * @param minValue - The minimum allowed value that the current value must be equal to or exceed.
 * @param currentValue - The value to validate against the minimum threshold.
 * @param language - The localized language configuration used to generate error messaging. Must include a valid structure for accessing `errors.form.underMin.errorText`.
 * @return An object containing `errorText`, a localized error message (empty if valid), and `isValid` indicating whether the validation passed.
 * @throws If the `language.errors.form.underMin.errorText` format or structure is invalid.
 */
export function minValueValidator(
    minValue: number,
    currentValue: number,
    language: Language
): ValidationResult {

    if( currentValue < minValue ) {
        return {
            errorText: language.errors.form.underMin.errorText.replace("$$[min]", minValue.toString() ),
            isValid: false
        };
    }

    return { errorText: "", isValid: true };

}

/**
 * Validates whether a given value falls within the specified minimum and maximum range.
 * Ensures accurate input validation to meet application requirements.
 *
 * This function combines both maximum and minimum validation checks, returning the result of the first violation if any.
 * If the value passes the maximum validation, it proceeds to validate against the minimum constraint.
 *
 * @param minMaxValue An object containing the `min` and `max` values to define the acceptable range.
 * - `min`: The minimum allowable value.
 * - `max`: The maximum allowable value.
 * @param currentValue The value to be validated against the range constraints.
 * @param language The language settings used to generate error messages or validation prompts.
 * @return An object indicating the validation result:
 * - If `isValid` is `true`: The value satisfies the range constraints.
 * - If `isValid` is `false`: The value violates either the minimum or maximum constraints.
 *   An appropriate validation error message is included.
 * @throws Will implicitly throw an error if underlying validators (maxValueValidator or minValueValidator) encounter unexpected conditions.
 */
export function minMaxValidator(
    minMaxValue: { max: number; min: number },
    currentValue: number,
    language: Language
): ValidationResult {

    const maxValid = maxValueValidator( minMaxValue.max, currentValue, language );

    if( ! maxValid.isValid ) {
        return maxValid;
    }

    return minValueValidator( minMaxValue.min, currentValue, language );


}